import React, { useEffect, useState } from 'react';
import { ArrowRightCircle, Edit, Plus, RefreshCw, Trash2, Search, Filter, Calendar, Target, TrendingUp, Users, CheckCircle, Clock, AlertCircle, ChevronLeft, ChevronRight, X, BookOpen, GraduationCap, Award, Info } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const PlansCarriere = () => {
  const { user } = useAuth();
  const { filialeId, currentFiliale } = useFiliale();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    personnel: [],
    filiales: []
  });
  const [formData, setFormData] = useState({
    personnel_id: '',
    filiale_id: filialeId || '',
    objectif_court_terme: '',
    objectif_moyen_terme: '',
    objectif_long_terme: '',
    competences_requises: '',
    formations_recommandees: '',
    statut: 'en_cours',
    date_debut: '',
    date_fin_prevue: ''
  });
  const [filters, setFilters] = useState({
    personnel_id: '',
    statut: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(1);
  const [showTooltip, setShowTooltip] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchDropdownData();
  }, [filialeId]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      if (filters.personnel_id) {
        params.personnel_id = filters.personnel_id;
      }
      if (filters.statut) {
        params.statut = filters.statut;
      }
      
      const queryString = new URLSearchParams(params).toString();
      const response = await http.get(`/api/plans-carriere${queryString ? '?' + queryString : ''}`);
      setPlans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const personnelUrl = (filialeId && filialeId !== 'all') 
        ? `/api/personnel?filiale_id=${filialeId}` 
        : '/api/personnel';
      
      const [personnel, filiales] = await Promise.all([
        http.get(personnelUrl),
        http.get('/api/filiales')
      ]);

      const personnelArray = Array.isArray(personnel.data) ? personnel.data : 
                             (personnel.data && personnel.data.data && Array.isArray(personnel.data.data) ? personnel.data.data : []);

      setDropdownData({
        personnel: personnelArray,
        filiales: Array.isArray(filiales.data) ? filiales.data : []
      });
    } catch (error) {
      console.error('Erreur chargement dropdown:', error);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setActiveTab(1);
    setFormData({
      personnel_id: '',
      filiale_id: filialeId || '',
      objectif_court_terme: '',
      objectif_moyen_terme: '',
      objectif_long_terme: '',
      competences_requises: '',
      formations_recommandees: '',
      statut: 'en_cours',
      date_debut: '',
      date_fin_prevue: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setActiveTab(1);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce plan de carrière ?')) {
      return;
    }
    try {
      await http.delete(`/api/plans-carriere/${id}`);
      fetchPlans();
    } catch (error) {
      console.error('Erreur suppression plan:', error);
      alert('Erreur lors de la suppression du plan');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/plans-carriere/${editingItem.id}`, formData);
      } else {
        await http.post('/api/plans-carriere', formData);
      }
      setShowModal(false);
      fetchPlans();
    } catch (error) {
      console.error('Erreur sauvegarde plan:', error);
      alert('Erreur lors de la sauvegarde du plan');
    }
  };

  // Filtrage et pagination
  const filteredPlans = plans.filter(p => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (p.prenom && p.prenom.toLowerCase().includes(searchLower)) ||
      (p.nom && p.nom.toLowerCase().includes(searchLower)) ||
      (p.objectif_court_terme && p.objectif_court_terme.toLowerCase().includes(searchLower))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlans = filteredPlans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);

  const getStatutColor = (statut) => {
    const colors = {
      'en_cours': 'bg-blue-100 text-blue-800',
      'atteint': 'bg-green-100 text-green-800',
      'en_retard': 'bg-yellow-100 text-yellow-800',
      'abandonne': 'bg-red-100 text-red-800'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800';
  };

  const getStatutIcon = (statut) => {
    const icons = {
      'en_cours': <Clock className="h-4 w-4" />,
      'atteint': <CheckCircle className="h-4 w-4" />,
      'en_retard': <AlertCircle className="h-4 w-4" />,
      'abandonne': <AlertCircle className="h-4 w-4" />
    };
    return icons[statut] || <Clock className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Plans de Carrière"
        description="Gestion des objectifs et développement professionnel"
        icon={Target}
        actions={[
          {
            label: 'Nouveau plan',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchPlans,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Filtres et recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-700">Filtres</h3>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employé</label>
              <select
                value={filters.personnel_id}
                onChange={(e) => setFilters({ ...filters, personnel_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                {dropdownData.personnel.map(p => (
                  <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={filters.statut}
                onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                <option value="en_cours">En cours</option>
                <option value="atteint">Atteint</option>
                <option value="en_retard">En retard</option>
                <option value="abandonne">Abandonné</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tableau des plans */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">
              Chargement...
            </div>
          ) : currentPlans.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">
              Aucun plan de carrière trouvé.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employé</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Objectifs court terme</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Objectifs moyen terme</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Objectifs long terme</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentPlans.map((plan) => (
                      <tr key={plan.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {plan.prenom} {plan.nom}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                          {plan.objectif_court_terme || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                          {plan.objectif_moyen_terme || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                          {plan.objectif_long_terme || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatutColor(plan.statut)}`}>
                            {getStatutIcon(plan.statut)}
                            {plan.statut.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(plan)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(plan.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredPlans.length)} sur {filteredPlans.length} plans
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Plan de Carrière */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingItem ? 'Modifier' : 'Créer'} Plan de Carrière
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-900"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Onglets */}
              <div className="border-b bg-white">
                <div className="flex">
                  <button
                    key="tab-1"
                    onClick={() => setActiveTab(1)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 1
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Informations générales
                  </button>
                  <button
                    key="tab-2"
                    onClick={() => setActiveTab(2)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 2
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Objectifs
                  </button>
                  <button
                    key="tab-3"
                    onClick={() => setActiveTab(3)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 3
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Développement
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Onglet 1: Informations générales */}
                  {activeTab === 1 && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-1">Informations générales</p>
                          <p>Sélectionnez l'employé concerné et définissez la période du plan de carrière. Le statut vous permet de suivre l'avancement du plan.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Employé *</label>
                          <select
                            value={formData.personnel_id}
                            onChange={(e) => setFormData({ ...formData, personnel_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.personnel.map(p => (
                              <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                          <select
                            value={formData.statut}
                            onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="en_cours">En cours</option>
                            <option value="atteint">Atteint</option>
                            <option value="en_retard">En retard</option>
                            <option value="abandonne">Abandonné</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                          <input
                            type="date"
                            value={formData.date_debut}
                            onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date fin prévue</label>
                          <input
                            type="date"
                            value={formData.date_fin_prevue}
                            onChange={(e) => setFormData({ ...formData, date_fin_prevue: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 2: Objectifs */}
                  {activeTab === 2 && (
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-purple-800">
                          <p className="font-semibold mb-1">Objectifs de carrière</p>
                          <p>Définissez les objectifs professionnels de l'employé sur trois horizons temporels. Soyez spécifique et mesurable dans vos objectifs pour faciliter le suivi.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <TrendingUp className="h-4 w-4 text-blue-600" />
                            Objectifs court terme (1-2 ans)
                          </label>
                          <textarea
                            value={formData.objectif_court_terme}
                            onChange={(e) => setFormData({ ...formData, objectif_court_terme: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Ex: Obtenir une certification en gestion de projet, mener 3 projets majeurs..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Target className="h-4 w-4 text-purple-600" />
                            Objectifs moyen terme (3-5 ans)
                          </label>
                          <textarea
                            value={formData.objectif_moyen_terme}
                            onChange={(e) => setFormData({ ...formData, objectif_moyen_terme: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Ex: Accéder à un poste de manager d'équipe, développer une expertise en analyse financière..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Award className="h-4 w-4 text-orange-600" />
                            Objectifs long terme (5+ ans)
                          </label>
                          <textarea
                            value={formData.objectif_long_terme}
                            onChange={(e) => setFormData({ ...formData, objectif_long_terme: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Ex: Devenir Directeur Département, siéger au comité de direction..."
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 3: Développement */}
                  {activeTab === 3 && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-green-800">
                          <p className="font-semibold mb-1">Développement professionnel</p>
                          <p>Identifiez les compétences clés à développer et les formations recommandées pour atteindre les objectifs définis précédemment.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <BookOpen className="h-4 w-4 text-green-600" />
                            Compétences requises
                          </label>
                          <textarea
                            value={formData.competences_requises}
                            onChange={(e) => setFormData({ ...formData, competences_requises: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Ex: Leadership, gestion budgétaire, communication interpersonnelle, maîtrise des outils de reporting..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <GraduationCap className="h-4 w-4 text-cyan-600" />
                            Formations recommandées
                          </label>
                          <textarea
                            value={formData.formations_recommandees}
                            onChange={(e) => setFormData({ ...formData, formations_recommandees: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Ex: Certification PMP, formation en finance avancée, coaching leadership, atelier de communication..."
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.max(1, activeTab - 1))}
                    disabled={activeTab === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.min(3, activeTab + 1))}
                    disabled={activeTab === 3}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingItem ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansCarriere;
